'use client';

import { useEffect, useState } from 'react';

import { Menu, Search } from 'lucide-react';
import { AppLink as Link } from '@/components/app-link';

import { Button } from '@naumu/ui/components/button';
import { Wordmark } from '@naumu/ui/components/wordmark';
import { DocsNavDrawer } from '@/modules/docs/components/docs-nav-drawer';
import { DocsSearch } from '@/modules/docs/components/docs-search';
import type { DocsNavGroup } from '@/modules/docs/helpers/docs-loader';

export const DocsHeader = ({
	navGroups,
}: {
	navGroups: DocsNavGroup[];
}): React.JSX.Element => {
	const [searchOpen, setSearchOpen] = useState(false);
	const [navOpen, setNavOpen] = useState(false);

	// ⌘K / Ctrl+K toggles the search palette.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
				event.preventDefault();
				setSearchOpen((prev) => !prev);
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, []);

	return (
		<>
			<header className="flex shrink-0 items-center gap-3 border-b border-neutral/10 bg-background px-4 py-4 md:gap-4 md:px-6">
				<Button
					variant="ghost"
					size="sm"
					iconOnly
					aria-label="Open navigation"
					onClick={() => setNavOpen(true)}
					className="md:hidden"
				>
					<Menu />
				</Button>

				<div className="flex shrink-0 items-center gap-2">
					<Link href="/">
						<Wordmark className="text-[15px] text-neutral" />
					</Link>
					<span aria-hidden className="select-none text-[15px] font-light text-neutral/25">
						/
					</span>
					<Link
						href="/docs"
						className="text-[15px] font-normal text-neutral/50 transition-colors hover:text-neutral/80"
					>
						docs
					</Link>
				</div>

				{/* Desktop quick links to the two entry points that matter most. */}
				<nav aria-label="Docs shortcuts" className="ml-2 hidden items-center gap-1 md:flex">
					<Link href="/docs/quickstart" className="shrink-0">
						<Button variant="ghost" size="sm" type="button">
							Getting started
						</Button>
					</Link>
					<Link href="/docs/repo-quickstart" className="shrink-0">
						<Button variant="ghost" size="sm" type="button">
							MCP
						</Button>
					</Link>
				</nav>

				<div className="flex-1" />

				<button
					type="button"
					onClick={() => setSearchOpen(true)}
					aria-label="Search docs"
					className="flex size-9 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-xl bg-surface text-neutral-muted transition-colors hover:bg-surface-strong md:w-auto md:min-w-[220px] md:justify-start md:px-3"
				>
					<Search className="size-[15px] shrink-0" strokeWidth={2} />
					<span className="hidden whitespace-nowrap text-[13px] md:inline">
						Search docs
					</span>
					<span className="ml-auto hidden rounded-sm border border-border-weak bg-background px-[5px] py-px text-[11px] font-semibold md:inline">
						⌘K
					</span>
				</button>

				<Link href="/home" className="shrink-0">
					<Button variant="solid-strong" size="sm" type="button">
						Open Naumu
					</Button>
				</Link>
			</header>

			<DocsSearch
				navGroups={navGroups}
				open={searchOpen}
				onClose={() => setSearchOpen(false)}
			/>

			<DocsNavDrawer
				navGroups={navGroups}
				open={navOpen}
				onClose={() => setNavOpen(false)}
			/>
		</>
	);
};
